// Global search / Ctrl+K command palette (documents parity sweep A7, #49) — searches documents
// (all 7 types at once), contacts (debtors + creditors) and products/items, and deep-links
// straight to the result. Ported from invoiceTest's src/globalsearch.js (Xero-style app-bar
// search over an in-memory index) — this build has no client-side document/contact/item cache,
// so the port queries the API as the user types (debounced) instead of filtering an index.
//
// ★ FOCUS TRAP — the #1 risk here (it has already bitten this sweep twice: A1's advanced-filter
// panel and A2's modals). This palette has a live search <input> the user types into across
// several debounced, async round-trips. If that input lived inside a reactive `${()=>}` view that
// re-renders when the query/results signals it reads change, tina4 would recreate the input node
// on every keystroke and drop focus after one character.
//
// Fix (the same one A2's openBulkPaymentModal/openCopyToModal in tenant/documents.js use): build
// the whole overlay — input, results container, everything — ONCE with html``, capture DOM
// handles via querySelector, and wire the input's input/keydown listeners IMPERATIVELY
// (addEventListener), never through tina4's own @input=/@keydown= reactive bindings. Nothing here
// is a signal; nothing about the input node is ever rebuilt while the palette is open. Only
// `resultsEl`'s CHILDREN are replaced as each debounced query settles (see renderGroups/
// renderLoading/renderIdle below) — the input itself is never touched. Proven by the FOCUS gate
// in test/globalsearch.test.js.
import { html, navigate } from './t4.js';
import { api } from './api.js';
import { t } from './i18n.js';
import { isAuthed } from './auth.js';
import { DOC_TYPES, statusLabel, isDraftDocNo } from '../tenant/documents.js';
import { fmtMoney, docCurrency, fmtIn, baseCurrency } from '../tenant/bank_helpers.js';
import { companyCurrencies, needsCurrencyLookup } from './currencies.js';

const DEBOUNCE_MS = 200;
const PER_GROUP = 8;   // top N per group — a palette, not a full list (each entity has its own list screen for that)

// type_code ('INV','QUO',…) -> docKey ('invoices','quotes',…) — derived from the single source of
// truth (documents.js's own exported DOC_TYPES map), the same way that file's own keyForType()
// does it locally, so this can never drift from the real routes in src/main.js. Computed LAZILY:
// this module is now also reached through tenant/tshell.js → vtsearch.js, and documents.js itself
// imports tshell — an import cycle in which DOC_TYPES is still undefined while this module
// evaluates. Deferring the read to first use breaks the eval-order dependency.
let _keyByType = null;
const KEY_BY_TYPE = () => (_keyByType ||= Object.fromEntries(Object.keys(DOC_TYPES).map(k => [DOC_TYPES[k].type, k])));

function currentCompanyId(){
  const path = typeof location !== 'undefined' ? location.pathname : '';
  const m = path.match(/^\/co\/([^/?]+)/);
  return m ? m[1] : null;
}

export function closeGlobalSearch(){
  const overlay = document.querySelector('.gsearch-overlay');
  if(overlay) overlay.remove();
}

function rowEl(label, sub, onSelect){
  const row = document.createElement('div');
  row.className = 'gsearch-row';
  row.setAttribute('role', 'option');
  row.tabIndex = -1;
  const main = document.createElement('span');
  main.className = 'gsearch-row-main';
  main.textContent = label;
  const subEl = document.createElement('span');
  subEl.className = 'gsearch-row-sub';
  subEl.textContent = sub;
  row.appendChild(main);
  row.appendChild(subEl);
  row.addEventListener('click', () => { onSelect(); closeGlobalSearch(); });
  return row;
}

function groupEl(titleText){
  const group = document.createElement('div');
  group.className = 'gsearch-group';
  const h = document.createElement('div');
  h.className = 'gsearch-group-title';
  h.textContent = titleText;
  group.appendChild(h);
  return group;
}

// Builds the three result groups from the settled API responses. A response that failed (network
// error, 401 already handled by shared/api.js, etc.) comes through as `null` — that group is just
// empty rather than throwing, so one dead lookup never blanks the whole palette.
function buildGroups(companyId, docsRes, custRes, suppRes, itemsRes, curs){
  // a document's total in ITS OWN currency (owner 2026-10-19: no currency bleeds into another)
  const docMoney = d => fmtIn(d.total, docCurrency(d, curs && curs.byId, (curs && curs.base) || baseCurrency()));
  const docRows = ((docsRes && docsRes.data) || [])
    .map(d => ({ d, key: KEY_BY_TYPE()[d.type_code] }))
    .filter(x => !!x.key)         // drop rows whose type has no live route to deep-link to (legacy codes)
    .slice(0, PER_GROUP)
    .map(({ d, key }) => {
      const cfg = DOC_TYPES[key];
      return {
        label: (isDraftDocNo(d.document_no) ? t('status.draft', 'Draft') : d.document_no) +
               ' — ' + (d.contact_name || '—'),
        sub: t(cfg.tnoun, cfg.noun) + ' · ' + docMoney(d) + ' · ' + statusLabel(d.display_status || d.status),
        onSelect: () => navigate('/co/' + companyId + '/' + key + '/' + d.id),
      };
    });

  // Contacts: search_contacts is type-scoped (debtor xor creditor), so two calls went out —
  // merge + dedupe by id (a contact CAN be both a customer and a supplier at once; that shows as
  // "Customer · Supplier", matching the source's own combined badge in invoiceTest/src/globalsearch.js).
  const byId = new Map();
  ((custRes && custRes.contacts) || []).forEach(c => byId.set(c.id, { c, cust: true, supp: false }));
  ((suppRes && suppRes.contacts) || []).forEach(c => {
    const ex = byId.get(c.id);
    if(ex) ex.supp = true; else byId.set(c.id, { c, cust: false, supp: true });
  });
  const contactRows = [...byId.values()].slice(0, PER_GROUP).map(({ c, cust, supp }) => ({
    label: c.name,
    sub: [cust ? t('contacts.customer', 'Customer') : '', supp ? t('contacts.supplier', 'Supplier') : '']
          .filter(Boolean).join(' · ') + (c.balance ? ' · ' + fmtMoney(c.balance) : ''),
    onSelect: () => navigate('/co/' + companyId + '/contacts/' + c.id),
  }));

  // Items/products: still no per-item route (no /co/{id}/products/{id} — the Products & Services
  // screen is one flat list/CRUD view, not per-item detail pages), so every item result deep-links
  // to the top-level Products & Services screen itself (documents parity sweep Wave B, #22 —
  // promoted from Settings → Stock; see tenant/products.js + tshell.js's `products` TNAV entry).
  const itemRows = ((itemsRes && itemsRes.data) || []).slice(0, PER_GROUP).map(it => ({
    label: it.name,
    sub: t('search.product', 'Product/service') +
         (it.price != null ? ' · ' + fmtMoney(it.price) + (it.unit ? ' / ' + it.unit : '') : ''),
    onSelect: () => navigate('/co/' + companyId + '/products'),
  }));

  return [
    { title: t('search.group_documents', 'Documents'), rows: docRows },
    { title: t('search.group_contacts', 'Contacts'), rows: contactRows },
    { title: t('search.group_items', 'Products'), rows: itemRows },
  ];
}

// The one place every consumer's lookup goes through (the Ctrl+K overlay below AND the vt
// app-bar's inline box — shared/vtsearch.js). Three entity kinds, fired in parallel per settled
// query (not chained) — contacts need TWO calls because search_contacts is type-scoped (debtor xor
// creditor; a company contact list has no "search both at once" endpoint). Each leg is caught
// individually so one dead lookup (e.g. a transient network error) degrades that group to empty
// instead of blanking the whole result set. Callers keep their own out-of-order sequence guards.
export async function searchEverything(companyId, q){
  const [docsRes, custRes, suppRes, itemsRes] = await Promise.all([
    api.get('/companies/' + companyId + '/documents?' + new URLSearchParams({ q, per_page: String(PER_GROUP) }))
      .catch(() => null),
    api.post('/companies/' + companyId + '/contacts/search', { search: q, contact_type: 0 }).catch(() => null),
    api.post('/companies/' + companyId + '/contacts/search', { search: q, contact_type: 1 }).catch(() => null),
    api.get('/companies/' + companyId + '/inventory/items?' + new URLSearchParams({ q })).catch(() => null),
  ]);
  // a hit that names its currency only by id needs the company's currency list to show its mark
  const curs = needsCurrencyLookup(docsRes && docsRes.data) ? await companyCurrencies(companyId) : null;
  return buildGroups(companyId, docsRes, custRes, suppRes, itemsRes, curs);
}

export function openGlobalSearch(companyId){
  if(!companyId) return;
  // Idempotent: a stray second Ctrl+K while the palette is already open just refocuses it — never
  // stacks a second overlay (the A2 modals never need this because they're opened from a click,
  // not a global chord that can fire repeatedly).
  const already = document.querySelector('.gsearch-overlay');
  if(already){ const inp = already.querySelector('.gsearch-input'); if(inp) inp.focus(); return; }

  const node = html`<div class="modal-overlay gsearch-overlay">
    <div class="modal-card modal-wide gsearch-card">
      <div class="gsearch-inputrow">
        <input type="search" class="form-control gsearch-input" data-gs-input autocomplete="off"
          placeholder=${t('search.placeholder', 'Search or jump — number, contact, amount…')}
          aria-label=${t('search.aria', 'Search everything (Ctrl+K or /)')}>
        <button type="button" class="modal-x" data-gs-close aria-label=${t('common.close', 'Close')}>✕</button>
      </div>
      <div class="gsearch-hint" data-gs-hint>${t('search.hint', 'Type a number, contact name or item — documents, contacts and products all search at once.')}</div>
      <div class="gsearch-results" data-gs-results></div>
    </div>
  </div>`;
  // html`` hands back a DocumentFragment for a multi-node root — appending it MOVES its children
  // out (emptying it), so the element left in the DOM afterwards must be captured up front. Same
  // "frag-emptied-on-append" trap openBulkPaymentModal's own comment documents in documents.js.
  const overlay = node.querySelector('.gsearch-overlay') || node;
  const inputEl = node.querySelector('[data-gs-input]');
  const resultsEl = node.querySelector('[data-gs-results]');
  const hintEl = node.querySelector('[data-gs-hint]');
  const closeBtn = node.querySelector('[data-gs-close]');

  let seq = 0;         // out-of-order guard — only the response to the LAST fired query may render
  let items = [];       // flattened selectable rows, in on-screen order, for ↑/↓/Enter
  let highlight = -1;

  function setHighlight(i){
    const rowNodes = items.map(it => it.el);
    rowNodes.forEach(el => el.classList.remove('kb-focus'));
    highlight = (i >= 0 && i < rowNodes.length) ? i : -1;
    if(highlight >= 0){
      rowNodes[highlight].classList.add('kb-focus');
      if(rowNodes[highlight].scrollIntoView) rowNodes[highlight].scrollIntoView({ block: 'nearest' });
    }
  }

  // Replaces ONLY resultsEl's children — the input above it is never touched, so it can never
  // lose focus here, no matter how many times a query round-trips.
  function renderGroups(groups){
    resultsEl.replaceChildren();
    items = [];
    const nonEmpty = groups.filter(g => g.rows.length);
    if(!nonEmpty.length){
      const empty = document.createElement('div');
      empty.className = 'muted-block';
      empty.textContent = t('search.empty', 'Nothing matches — try a number, contact or item');
      resultsEl.appendChild(empty);
      highlight = -1;
      return;
    }
    nonEmpty.forEach(g => {
      const grp = groupEl(g.title);
      g.rows.forEach(r => {
        const el = rowEl(r.label, r.sub, r.onSelect);
        grp.appendChild(el);
        items.push({ el, onSelect: r.onSelect });
      });
      resultsEl.appendChild(grp);
    });
    setHighlight(items.length ? 0 : -1);
  }

  function renderLoading(){
    resultsEl.replaceChildren();
    const el = document.createElement('div');
    el.className = 'muted-block';
    el.textContent = t('common.loading', 'Loading…');
    resultsEl.appendChild(el);
    items = []; highlight = -1;
  }

  function renderIdle(){
    resultsEl.replaceChildren();
    items = []; highlight = -1;
  }

  async function runSearch(term){
    const q = term.trim();
    const mySeq = ++seq;
    if(!q){ hintEl.hidden = false; renderIdle(); return; }
    hintEl.hidden = true;
    renderLoading();
    const groups = await searchEverything(companyId, q);
    // A newer keystroke has since fired its own query — this response lost the race, discard it
    // rather than flash stale results over whatever the latest query is about to render.
    if(mySeq !== seq) return;
    renderGroups(groups);
  }

  let debounceTimer = null;
  inputEl.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    const term = inputEl.value;
    debounceTimer = setTimeout(() => runSearch(term), DEBOUNCE_MS);
  });
  inputEl.addEventListener('keydown', e => {
    if(e.key === 'ArrowDown'){ e.preventDefault(); setHighlight(Math.min(highlight + 1, items.length - 1)); }
    else if(e.key === 'ArrowUp'){ e.preventDefault(); setHighlight(Math.max(highlight - 1, 0)); }
    else if(e.key === 'Enter'){
      e.preventDefault();
      const it = items[highlight] || items[0];
      if(it){ it.onSelect(); closeGlobalSearch(); }
    } else if(e.key === 'Escape'){ e.preventDefault(); closeGlobalSearch(); }
  });
  closeBtn.addEventListener('click', closeGlobalSearch);
  // Click the backdrop (not the card) to dismiss — mirrors every other overlay's ✕/Cancel-only
  // close, just extended to the empty area, since this one has no explicit Cancel button.
  overlay.addEventListener('mousedown', e => { if(e.target === overlay) closeGlobalSearch(); });

  document.body.appendChild(overlay);
  inputEl.focus();
}

// Ctrl+K / Cmd+K — a chord, so it fires even while typing in an unrelated field (preventDefault
// must win over the browser's own Ctrl+K) — or "/", guarded like every other single-key shortcut
// in this app (globalkb.js's n/q/g): never while a text field/select/contenteditable already has
// focus, so it never steals a literal "/" the user is typing. Registered ONCE at boot.
export function globalSearchKbHandler(e){
  const chord = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k';
  if(!chord){
    if(e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
    const active = document.activeElement;
    if(active && (/^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName) || active.isContentEditable)) return;
  }
  if(!isAuthed()) return;
  const companyId = currentCompanyId();
  if(!companyId) return;             // not on a /co/{id}/… screen — no company to search within
  e.preventDefault();
  // vt (invoiceTest-skin) screens carry the search INLINE in the app bar — the chord focuses that
  // box, exactly like the sandbox, instead of opening the overlay palette.
  const inline = document.body.classList.contains('vt') && document.querySelector('.global-search .ss-input');
  if(inline){ inline.focus(); return; }
  openGlobalSearch(companyId);
}

// Stash-and-replace, same house pattern as globalkb.js's initGlobalShortcuts — re-calling this
// (HMR, a stray second boot) must never leave two listeners attached and double-triggering.
// Deliberately a SEPARATE listener/module from globalkb.js: that one owns the plain keys n/q/g,
// this one owns the Ctrl+K chord + "/", so the two never have to know about each other.
export function initGlobalSearch(){
  if(window.__globalSearchHandler) document.removeEventListener('keydown', window.__globalSearchHandler);
  window.__globalSearchHandler = globalSearchKbHandler;
  document.addEventListener('keydown', globalSearchKbHandler);
}
